// AI Academic Content Engine — Task Type Configuration
// Maps each task type to its schema, UI layout, inputs, and prompt strategy

export const TASK_TYPE_CONFIG = {
  Assignment: {
    icon: 'FileText',
    purpose: 'Practice + reinforcement',
    description: 'Section-based questions with short, long, and application types',
    sourceRequired: false,
    inputs: [
      { key: 'difficulty', label: 'Difficulty', type: 'select', options: ['Easy', 'Medium', 'Hard'] },
      { key: 'questionCount', label: 'Total Questions', type: 'number', default: 10 },
      { key: 'maxMarks', label: 'Maximum Marks', type: 'number', default: 50 },
      { key: 'topicFocus', label: 'Topic Focus', type: 'text', placeholder: 'e.g. Photosynthesis, Quadratic Equations' },
    ],
    outputLayout: 'sections',
    sections: [
      { key: 'sectionA', label: 'Section A — Short Answers', questionType: 'Short Answer' },
      { key: 'sectionB', label: 'Section B — Long Answers', questionType: 'Long Answer' },
      { key: 'sectionC', label: 'Section C — Application Based', questionType: 'Application' },
    ],
  },
  Test: {
    icon: 'Clock',
    purpose: 'Short evaluation',
    description: 'Timer-based with MCQs, short answers, and quick checks',
    sourceRequired: false,
    inputs: [
      { key: 'difficulty', label: 'Difficulty', type: 'select', options: ['Easy', 'Medium', 'Hard'] },
      { key: 'timeLimit', label: 'Time Limit (mins)', type: 'number', default: 30 },
      { key: 'mcqCount', label: 'MCQ Count', type: 'number', default: 10 },
      { key: 'shortCount', label: 'Short Answer Count', type: 'number', default: 5 },
      { key: 'maxMarks', label: 'Maximum Marks', type: 'number', default: 30 },
    ],
    outputLayout: 'timer-cards',
  },
  Exam: {
    icon: 'GraduationCap',
    purpose: 'Formal evaluation',
    description: 'Board-style structured paper with 4 sections',
    sourceRequired: false,
    inputs: [
      { key: 'difficulty', label: 'Difficulty', type: 'select', options: ['Easy', 'Medium', 'Hard'] },
      { key: 'questionCount', label: 'Total Questions', type: 'number', default: 30 },
      { key: 'maxMarks', label: 'Maximum Marks', type: 'number', default: 100 },
      { key: 'examFormat', label: 'Format', type: 'select', options: ['CBSE Board', 'ICSE Board', 'IGCSE (Cambridge)', 'IB (MYP/DP)', 'CIS (International)', 'State Board', 'Custom'] },
      { key: 'duration', label: 'Duration (hrs)', type: 'number', default: 3 },
    ],
    outputLayout: 'board-paper',
    sections: [
      { key: 'sectionA', label: 'Section A — MCQs', questionType: 'MCQ' },
      { key: 'sectionB', label: 'Section B — Short Answers', questionType: 'Short Answer' },
      { key: 'sectionC', label: 'Section C — Long Answers', questionType: 'Long Answer' },
      { key: 'sectionD', label: 'Section D — Case Study', questionType: 'Case Study' },
    ],
  },
  Homework: {
    icon: 'Home',
    purpose: 'Daily reinforcement',
    description: 'Small, simple tasks — 5-10 questions with reading + writing',
    sourceRequired: false,
    inputs: [
      { key: 'taskCount', label: 'Number of Tasks', type: 'number', default: 7 },
      { key: 'difficulty', label: 'Difficulty', type: 'select', options: ['Easy', 'Medium'] },
      { key: 'includeReading', label: 'Include Reading Tasks', type: 'toggle', default: true },
    ],
    outputLayout: 'checklist',
  },
  'Holiday Homework': {
    icon: 'Sun',
    purpose: 'Engagement + creativity',
    description: 'Activity-based: scrapbook, observations, projects, family tasks',
    sourceRequired: false,
    inputs: [
      { key: 'activityCount', label: 'Number of Activities', type: 'number', default: 5 },
      { key: 'creativityLevel', label: 'Creativity Level', type: 'select', options: ['Simple', 'Moderate', 'Highly Creative'] },
      { key: 'durationDays', label: 'Duration (days)', type: 'number', default: 14 },
    ],
    outputLayout: 'activity-cards',
  },
  Debate: {
    icon: 'MessageSquare',
    purpose: 'Critical thinking + speaking',
    description: '2-column FOR vs AGAINST with arguments and rebuttals',
    sourceRequired: false,
    inputs: [
      { key: 'argumentCount', label: 'Arguments per side', type: 'number', default: 4 },
      { key: 'tone', label: 'Tone', type: 'select', options: ['Academic', 'Persuasive', 'Balanced'] },
      { key: 'includeRebuttals', label: 'Include Rebuttals', type: 'toggle', default: true },
    ],
    outputLayout: 'debate-columns',
  },
  'Play / Skit': {
    icon: 'Theater',
    purpose: 'Creativity + performance',
    description: 'Script with characters, dialogues, and scene breakdown',
    sourceRequired: false,
    inputs: [
      { key: 'characterCount', label: 'Number of Characters', type: 'number', default: 4 },
      { key: 'duration', label: 'Duration (mins)', type: 'number', default: 15 },
      { key: 'humor', label: 'Humor Level', type: 'select', options: ['Serious', 'Light', 'Comedy'] },
      { key: 'sceneCount', label: 'Scenes', type: 'number', default: 3 },
    ],
    outputLayout: 'script',
  },
  'Book / Chapter Task': {
    icon: 'BookOpen',
    purpose: 'Reading comprehension',
    description: 'Analyze selected book/chapter — Understand, Analyze, Reflect',
    sourceRequired: true,
    inputs: [
      { key: 'depth', label: 'Analysis Depth', type: 'select', options: ['Basic', 'Intermediate', 'Deep'] },
      { key: 'includeCharacterAnalysis', label: 'Character Analysis', type: 'toggle', default: true },
    ],
    outputLayout: 'analysis-panels',
    panels: [
      { key: 'understand', label: 'Understand', icon: 'BookOpen' },
      { key: 'analyze', label: 'Analyze', icon: 'Search' },
      { key: 'reflect', label: 'Reflect', icon: 'Lightbulb' },
    ],
  },
  Summary: {
    icon: 'AlignLeft',
    purpose: 'Concept clarity',
    description: 'Bullet summary, key points, mind-map style notes',
    sourceRequired: true,
    inputs: [
      { key: 'length', label: 'Summary Length', type: 'select', options: ['Brief', 'Standard', 'Detailed'] },
      { key: 'includeKeywords', label: 'Highlight Keywords', type: 'toggle', default: true },
    ],
    outputLayout: 'notes',
  },
  Custom: {
    icon: 'Settings',
    purpose: 'Flexibility',
    description: 'User-defined format with modular section builder',
    sourceRequired: false,
    inputs: [
      { key: 'instructions', label: 'Custom Instructions', type: 'textarea', placeholder: 'Describe what you want the AI to generate...' },
    ],
    outputLayout: 'modular',
  },
};

export const TASK_TYPES_LIST = Object.keys(TASK_TYPE_CONFIG);

export const SOURCE_TYPES = {
  library: { label: 'Library', description: 'Browse Gutenberg & NCERT books', icon: 'Library' },
  pdf: { label: 'Upload PDF', description: 'Upload & extract content from PDF', icon: 'Upload' },
  topic: { label: 'Topic Only', description: 'Generate from topic (no source)', icon: 'Lightbulb' },
};
